import { showBackofficeFlash } from "packs/init-backoffice-flashes"
import { confirmBackofficeDelete } from "packs/init-backoffice-confirm"
import { applyWizardChecklist } from "packs/init-backoffice-autosave"

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

const amenitiesForm = () => document.getElementById("edition-amenities-form")

const syncAmenitiesEmptyState = () => {
  const groups = document.querySelector("[data-amenity-groups]")
  const empty = document.querySelector("[data-amenities-empty]")
  if (!groups) return

  const hasRows = groups.querySelectorAll("[data-amenity-id]").length > 0
  groups.hidden = !hasRows
  if (empty) empty.hidden = hasRows
}

const syncAmenitiesIntroNote = () => {
  const note = document.querySelector("[data-amenities-intro-note]")
  const form = amenitiesForm()
  if (!note || !form) return

  const checkboxes = document.querySelectorAll(
    `input[type="checkbox"][name*="tarifs_checklist"][form="${form.id}"]`
  )
  note.hidden = checkboxes.length > 0 && Array.from(checkboxes).every((box) => box.checked)
}

const applyPricingChecklist = (items) => {
  if (!items) return

  items.forEach(({ key, confirmed }) => {
    const checkbox = document.querySelector(
      `input[type="checkbox"][name="edition[tarifs_checklist][${key}]"]`
    )
    const control = checkbox?.closest(".backoffice-pricing-checklist__control")
    if (!checkbox || !control) return

    checkbox.checked = Boolean(confirmed)
    if (confirmed) {
      const year = amenitiesForm()?.dataset?.editionYear || ""
      control.setAttribute("data-tooltip", `Information confirmée pour l'édition ${year} ! 🎉`)
    } else {
      control.setAttribute(
        "data-tooltip",
        "Vérifiez la prestation, puis cochez pour confirmer."
      )
    }
  })

  syncAmenitiesIntroNote()
}

const syncCategoryCount = (group) => {
  if (!group) return
  const count = group.querySelectorAll("[data-amenity-id]").length
  const countEl = group.querySelector("[data-amenity-category-count]")
  if (!countEl) return
  countEl.textContent = count > 1 ? `(${count} prestations)` : `(${count} prestation)`
}

const insertAmenityRow = (categoryId, html) => {
  const group = document.querySelector(`[data-amenity-category-id="${categoryId}"]`)
  const rows = group?.querySelector("[data-amenity-rows]")
  if (!rows || !html) return false

  rows.insertAdjacentHTML("beforeend", html)
  if (group && "open" in group) group.open = true
  syncCategoryCount(group)
  syncAmenitiesEmptyState()
  return true
}

const removeAmenityRow = (amenityId) => {
  const row = document.querySelector(`[data-amenity-id="${amenityId}"]`)
  const group = row?.closest("[data-amenity-category-id]")
  row?.remove()
  syncCategoryCount(group)
  syncAmenitiesEmptyState()
}

const bindAddForm = (form) => {
  if (!form || form.dataset.amenityAjaxBound === "true") return
  form.dataset.amenityAjaxBound = "true"

  form.addEventListener("submit", async (event) => {
    event.preventDefault()
    const submit = form.querySelector('input[type="submit"], button[type="submit"]')
    if (submit) submit.disabled = true

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken() || "",
        },
        credentials: "same-origin",
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        showBackofficeFlash(data.error || "Impossible d'ajouter la prestation", "alert")
        return
      }

      const inserted = insertAmenityRow(data.category_id, data.html)
      if (!inserted) {
        window.location.reload()
        return
      }

      applyPricingChecklist(data.tarifs_checklist)
      applyWizardChecklist(data.checklist, data.progress_percent)
      form.reset()
      showBackofficeFlash(data.notice || "Prestation ajoutée", "notice")
    } catch (_) {
      showBackofficeFlash("Impossible d'ajouter la prestation", "alert")
    } finally {
      if (submit) submit.disabled = false
    }
  })
}

const destroyAmenity = async (link) => {
  const message = link.dataset.confirmMessage || "Supprimer cette prestation ?"
  const confirmed = await confirmBackofficeDelete({
    title: "Supprimer la prestation ?",
    text: message,
    confirmButtonText: "Supprimer",
  })
  if (!confirmed) return

  try {
    const response = await fetch(link.href, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken() || "",
      },
      credentials: "same-origin",
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      showBackofficeFlash(data.error || "Impossible de supprimer la prestation", "alert")
      return
    }

    removeAmenityRow(data.amenity_id)
    applyPricingChecklist(data.tarifs_checklist)
    applyWizardChecklist(data.checklist, data.progress_percent)
    showBackofficeFlash(data.notice || "Prestation supprimée", "notice")
  } catch (_) {
    showBackofficeFlash("Impossible de supprimer la prestation", "alert")
  }
}

export const initBackofficeAmenities = () => {
  if (!document.querySelector("[data-amenity-groups], [data-amenity-add-form]")) return

  document.querySelectorAll("[data-amenity-add-form]").forEach(bindAddForm)

  if (document.body.dataset.amenityDeleteDelegationBound === "true") return
  document.body.dataset.amenityDeleteDelegationBound = "true"

  document.addEventListener("click", (event) => {
    const link = event.target.closest("[data-amenity-delete]")
    if (!link) return
    event.preventDefault()
    event.stopPropagation()
    destroyAmenity(link)
  })
}
